import type { CSSProperties } from 'react';
import { useState } from 'react';

import type { Maybe, StringOrNode, TypoColor, TypoSize, TypoWeight } from '@snowit/icetypes';

import copyToClipboard from '@snowit/utils/copyToClipboard';

import ButtonIcon from '../../Buttons/ButtonIcon';
import Typo from '../../Typography/Typo';

import cx from 'classnames';
import s from './TinyTable.module.scss';

export interface TinyTableData {
  key: string;
  label?: string;
  value?: StringOrNode;
  onClick?: () => void;
  copyValue?: Maybe<string>;
  hidden?: boolean;
}
export interface ITinyTable {
  data: TinyTableData[];
  sizes?: TypoSize[];
  colors?: TypoColor[];
  weights?: TypoWeight[];
  columns?: string[];
  gap?: number[];
  classNames?: {
    wrapper?: string;
    table?: string;
    label?: string;
    wrapValue?: string;
    value?: string;
    copyButton?: string;
  };
  style?: {};
}

const TinyTable = (props: ITinyTable) => {
  const {
    data,
    sizes = ['df'],
    colors = ['text-l', 'text'],
    columns = ['8rem', '1fr'],
    weights = ['regular'],
    gap = [0.5],
    classNames,
    style,
  } = props;
  const [copied, setCopied] = useState<string>('');
  const tableStyle = {
    '--l-col-first': columns?.[0],
    '--l-col-second': columns?.[1],
    '--l-row-gap': `${gap[0]}rem`,
    '--l-col-gap': `${gap[1] ?? gap[0]}rem`,
  } as CSSProperties;

  const handleCopy = (item: TinyTableData): void => {
    copyToClipboard(item.copyValue || '', () => {
      setCopied(item.key);
      setTimeout(() => {
        setCopied('');
      }, 2000);
    });
  };

  const renderCopy = (item: TinyTableData) => {
    if (!item.copyValue) return null;
    return (
      <ButtonIcon
        classNames={{ button: classNames?.copyButton }}
        icon={copied === item.key ? 'check_circle' : 'copy'}
        onClick={({ event }) => {
          event.stopPropagation();
          handleCopy(item);
        }}
        theme={'ghost'}
        size={'xxl'}
        color={copied === item.key ? 'ok' : 'text'}
      />
    );
  };

  const renderLabel = (label?: string) => {
    if (!label) return null;
    return (
      <Typo
        className={cx(s.label, classNames?.label)}
        text={label}
        tag={'p'}
        size={sizes[0]}
        color={colors[0]}
        weight={weights[0]}
      />
    );
  };

  const renderValue = (value: StringOrNode) => {
    if (!value) return null;

    if (typeof value === 'string' || typeof value === 'number') {
      return (
        <Typo
          className={cx(s.value, classNames?.value)}
          text={value}
          tag={'p'}
          size={sizes[1] || sizes[0]}
          color={colors[1] || colors[0]}
          weight={weights[1] || weights[0]}
        />
      );
    }

    if (typeof value === 'function') {
      return value();
    }

    return value;
  };

  return (
    <div
      className={cx(s.wrapper, classNames?.wrapper, {
        [s.oneCol]: columns.length === 1,
      })}
      style={style}
    >
      {data
        .filter((info) => !info.hidden)
        .map((item) => {
          return (
            <div
              key={item.key}
              className={cx(s.table, classNames?.table, {
                [s.copy]: item.copyValue,
                [s.link]: Boolean(item.onClick),
              })}
              style={tableStyle}
            >
              {renderLabel(item.label)}
              <div className={cx(s.wrapValue, classNames?.wrapValue)} onClick={item.onClick}>
                {renderValue(item.value)}
                {renderCopy(item)}
              </div>
            </div>
          );
        })}
    </div>
  );
};

export default TinyTable;
